
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface FileMetadata {
  id: string;
  name: string;
  type: string;
  size: number;
  created_at: string;
  owner_id: string;
  url: string;
  shared_with?: string[];
  is_public: boolean;
}

export const uploadFile = async (
  file: File,
  folder: string = 'general',
  metadata: Record<string, any> = {}
): Promise<FileMetadata | null> => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    const filePath = `${folder}/${fileName}`;
    
    // Get user ID for owner_id
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error("User not authenticated");
    }
    
    // Upload the file to storage
    const { data: uploadData, error: uploadError } = await supabase
      .storage
      .from('files')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type
      });
    
    if (uploadError) {
      throw new Error(`Error uploading file: ${uploadError.message}`);
    }
    
    // Get the public URL
    const { data: urlData } = supabase
      .storage
      .from('files')
      .getPublicUrl(filePath);
    
    // Create a record in the files table
    const { data: fileData, error: fileError } = await supabase
      .from('files')
      .insert({
        name: file.name,
        path: filePath,
        size: file.size,
        type: file.type,
        url: urlData.publicUrl,
        metadata: metadata,
        is_public: false,
        owner_id: user.id // Add the owner_id here
      })
      .select()
      .single();
    
    if (fileError) {
      throw new Error(`Error creating file record: ${fileError.message}`);
    }
    
    return fileData as FileMetadata;
  } catch (error) {
    console.error("Error in uploadFile:", error);
    toast.error(`Fehler beim Hochladen: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    return null;
  }
};

export const getFiles = async (ownerId?: string): Promise<FileMetadata[]> => {
  try {
    let query = supabase
      .from('files')
      .select('*');
    
    // If owner ID is provided, filter by owner
    if (ownerId) {
      query = query.eq('owner_id', ownerId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      throw error;
    }
    
    return data as FileMetadata[];
  } catch (error) {
    console.error("Error fetching files:", error);
    toast.error("Fehler beim Laden der Dateien");
    return [];
  }
};

export const deleteFile = async (id: string): Promise<boolean> => {
  try {
    // Get the file path first
    const { data: fileData, error: fetchError } = await supabase
      .from('files')
      .select('path')
      .eq('id', id)
      .single();
    
    if (fetchError) {
      throw fetchError;
    }
    
    // Delete from storage
    const { error: storageError } = await supabase
      .storage
      .from('files')
      .remove([fileData.path]);
    
    if (storageError) {
      throw storageError;
    }
    
    // Delete from database
    const { error: dbError } = await supabase
      .from('files')
      .delete()
      .eq('id', id);
    
    if (dbError) {
      throw dbError;
    }
    
    return true;
  } catch (error) {
    console.error("Error deleting file:", error);
    toast.error("Fehler beim Löschen der Datei");
    return false;
  }
};

export const shareFile = async (fileId: string, userIds: string[]): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('file_shares')
      .insert(userIds.map(userId => ({
        file_id: fileId,
        user_id: userId
      })));
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error("Error sharing file:", error);
    toast.error("Fehler beim Teilen der Datei");
    return false;
  }
};

export const setFilePublic = async (fileId: string, isPublic: boolean): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('files')
      .update({ is_public: isPublic })
      .eq('id', fileId);
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error("Error setting file public status:", error);
    toast.error("Fehler beim Ändern der Freigabeeinstellungen");
    return false;
  }
};
